import { computed, reactive, ref, readonly } from "vue";
import * as API from "@/api/API";
import Compressor from "@/helpers/data/Compressor";
import { ModelNotFoundError, NotFoundError } from "@core/errors/Errors";
import { cloudMediaAPI } from "@core/media/media.store";
import { tryWithPasswordHeader } from "@/helpers/data/tryWithPassword";

import type { PublicModel } from "./PublicModel";
import type { ModelVersionMeta } from "./ModelVersion";

const publicModels = reactive<Record<string, PublicModel>>({});
const loadingIds = ref(new Set());

const passwordsInCache: Record<string, string> = {};

export function usePublicModels(props?: { publicationId?: string }) {
  // COMPUTED
  const publicModelsCount = computed(() => Object.values(publicModels).length);
  const publicModel = computed(() =>
    props?.publicationId ? publicModels[props.publicationId] : undefined
  );

  const isLoading = computed(() => loadingIds.value.has(props?.publicationId));

  // METHODS

  /**
   * Get all public models that can be retrieved from the platform
   * (lists all example models)
   */
  async function loadPublicModels() {
    const models = await API.get<PublicModel[]>("/v1/public-models");
    if (!models) return [];

    for (const model of models) {
      publicModels[model.id] = model;
    }

    return readonly(models);
  }

  /**
   *
   */
  async function getPublicModelInfo(
    publicationId: string,
    requestOptions: API.RequestOptions = {}
  ) {
    loadingIds.value.add(publicationId);
    try {
      const publicModel = await makePublicModelRequest<PublicModel>(
        "GET",
        publicationId,
        "",
        requestOptions
      );
      publicModels[publicModel.id] = publicModel;

      return readonly(publicModels)[publicModel.id];
    } finally {
      loadingIds.value.delete(publicationId);
    }
  }

  /**
   *
   */
  async function getPublicModelData(
    publicationId: string,
    requestOptions: API.RequestOptions = {}
  ) {
    const metaData = await makePublicModelRequest<ModelVersionMeta>(
      "GET",
      publicationId,
      "/modeldata/meta",
      requestOptions
    );
    const modelData = await makePublicModelRequest<string>(
      "GET",
      publicationId,
      "/modeldata",
      {
        ...requestOptions,
        responseType: "text",
        parseResponse: (response) => {
          return Compressor.decompress(response.text);
        }
      }
    );

    // load all model media files
    const mediaEntries = await cloudMediaAPI
      .loadModelMedia(metaData.media, requestOptions)
      .catch((error) => {
        console.warn(error);
        return [] as {
          uuid: string;
          data: Blob;
          uploaded: boolean;
        }[];
      });

    return {
      id: metaData.id,
      data: modelData,
      media: mediaEntries
    };
  }

  async function copyPublicModel(
    publication: PublicModel,
    copyData: Partial<PublicModel>
  ) {
    return makePublicModelRequest<PublicModel>(
      "POST",
      publication.id,
      "/copy",
      {
        data: copyData,
        sendAuthToken: true
      }
    );
  }

  return {
    publicModel,
    publicModels,
    publicModelsCount,
    loadPublicModels,

    isLoading,

    getPublicModelInfo,
    getPublicModelData,

    copyPublicModel
  };
}

/**
 *
 */
async function makePublicModelRequest<T>(
  method: "GET" | "POST" | "PUT",
  publicationId: string,
  address: string,
  requestOptions: API.RequestOptions<T>
) {
  try {
    return await tryWithPasswordHeader(
      passwordsInCache[publicationId],
      async (headers) => {
        return await API.sendRequest<T>(
          method,
          `/v1/public-models/${publicationId}${address}`,
          {
            sendAuthToken: false,
            ...requestOptions,
            headers: {
              ...headers,
              ...requestOptions?.headers
            }
          }
        );
      },
      (password) => (passwordsInCache[publicationId] = password)
    );
  } catch (error) {
    if (error instanceof NotFoundError) {
      throw new ModelNotFoundError(error.message);
    }
    throw error;
  }
}
